import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
  Input,
  HostListener,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs';

import { Map } from 'mapbox-gl';
import { PopupComponent } from 'ngx-mapbox-gl';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { environment } from '@env/environment';
import UrlUtils from 'app/shared/Utils/url.utils';
import { AddressPart } from 'app/shared/models';
import { Property } from 'app/modules/projects/projects.types';
import { Parcel, LatLng } from 'app/modules/research/research.types';
import { floodMapPaintConfig } from 'app/modules/research/@components/flood-map-legend/flood-map-paint-config';
import { floodMapLegendItems } from 'app/modules/research/@components/flood-map-legend/flood-map-legend-items';

@Component({
  selector: 'parcel-map',
  templateUrl: './parcel-map.component.html',
  styleUrls: ['./parcel-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParcelMapComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  @Input() properties: Property[] = [];
  @Input() center: LatLng = { latitude: 39.09747, longitude: -94.57989 };
  @Input() allowClickEvent = true;
  @Input() showPin = true;

  @Output() onSelectParcelEvent = new EventEmitter<any>();
  @Output() onDeSelectParcelEvent = new EventEmitter<any>();
  @Output() onSelectLatLngEevnt = new EventEmitter<any>();

  @ViewChild('viewer') viewer: ElementRef;
  @ViewChildren(PopupComponent) popups: QueryList<PopupComponent>;

  public floodTileServerUrl = 'https://tiles.termsheet.com/flood';
  public map: Map;
  public parcels: Parcel[];
  public selectedParcels: any[] = [];
  public showFloodMap: boolean = true;
  public floodMapOverlayImages = floodMapLegendItems;
  public layerFlood = floodMapPaintConfig;
  public parcelTilesVisible: string = 'visible';
  public searchForm: UntypedFormGroup;
  public fullScreenMode = false;
  public parceledTileServerUrl = `${environment.tilesApi.url}`;
  public popupStates: boolean[] = [];
  public layerClicked = false;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.showFloodMap = UrlUtils.getFromLocalStorage('ParcelMapComponentFloodMap');
    this.buildForm();
    this.searchForm
      .get('flood_map')
      .valueChanges.pipe(untilComponentDestroyed(this), debounceTime(300))
      .subscribe((value) => {
        this.showFloodMap = value;
        UrlUtils.saveToLocalStorage('ParcelMapComponentFloodMap', value);
        this.changeDetectorRef.markForCheck();
      });
  }

  private buildForm(): void {
    this.searchForm = this.formBuilder.group({
      address: [''],
      flood_map: [this.showFloodMap],
    });
  }

  @HostListener('window:fullscreenchange', ['$event'])
  handleFullScreenMode(): void {
    this.fullScreenMode = !this.fullScreenMode && this.showFloodMap;
  }

  public setAddressParts(addressParts: AddressPart): void {
    if (addressParts.lat && addressParts.lng) {
      if (this.map) {
        this.center = { latitude: addressParts.lat, longitude: addressParts.lng };
        this.map.jumpTo({ center: [addressParts.lng, addressParts.lat], zoom: 15 });
      }
    }
    this.changeDetectorRef.markForCheck();
  }

  public onPopupOpen(index: number): void {
    this.popupStates[index] = true;
  }

  public onPopupClose(index: number): void {
    this.popupStates[index] = false;
  }

  public onParcelClick(event: any): void {
    this.layerClicked = true;
    if (this.allowClickEvent) {
      const feature = event.features[0];
      const index = this.selectedParcels.findIndex((option) => option?.id === feature.properties.id);
      if (index >= 0) {
        this.selectedParcels.splice(index, 1);
        this.onDeSelectParcelEvent.emit(event);
      } else {
        this.selectedParcels.push({
          id: feature.properties.id,
          apn: feature.properties.apn,
          lat: event.lngLat.lat,
          lng: event.lngLat.lng,
        });
        this.onSelectParcelEvent.emit(event);
      }
    }
  }

  public onMapClick(event: any): void {
    if (event instanceof PointerEvent) {
      return;
    }
    this.layerClicked = false;
    setTimeout(() => {
      if (this.layerClicked) {
        return;
      }
      this.selectedParcels.push({
        id: null,
        apn: null,
        lat: event.lngLat.lat,
        lng: event.lngLat.lng,
      });
      this.onSelectLatLngEevnt.emit(event);
    }, 1000);
  }

  public onParcelLayerMouseMove(): void {
    this.map.getCanvas().style.cursor = 'pointer';
  }

  public onParcelLayerMouseLeave(): void {
    this.map.getCanvas().style.cursor = '';
  }

  public onMapLoad(mapInstance: Map): void {
    this.map = mapInstance;
    this.map.resize();
  }

  onTransformMapRequest(url: string, resourceType): any {
    if (url.includes(environment.tilesApi.url)) {
      return {
        url: url,
        headers: { Auth: `Bearer ${environment.tilesApi.authToken}` },
      };
    }
  }
}
